import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  CircularProgress,
  Typography,
  IconButton,
  Stack,
  Button,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import Response from './Response';
import EndpointArgs from './EndpointArgs';

export default function handler({
  endpoints,
  responses,
  fetchDataForEndpoint,
  setEndpointArgs,
  loading,
  handleOpenModal,
}) {
  const [expanded, setExpanded] = useState({});
  const [minimalMode, setMinimalMode] = useState(false);

  const pathToTitle = (path) => path
    .substr(1)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const toggleAccordion = (endpoint) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [endpoint]: isExpanded }));
    if (isExpanded) {
      fetchDataForEndpoint(endpoint);
    }
  };

  const refreshAll = () => {
    Object.keys(expanded).forEach((endpoint) => {
      if (expanded[endpoint]) {
        fetchDataForEndpoint(endpoint);
      }
    });
  };

  const collapseAll = () => {
    setExpanded({});
  };

  const mathJaxConfig = {
    loader: { load: ['input/tex', 'output/chtml'] },
    tex: {
      inlineMath: [['$', '$']],
      displayMath: [['$$', '$$']],
      processEscapes: true,
    },
  };

  return (
    <Box sx={{ my: 2 }}>
      <FormControlLabel
        control={<Switch />}
        label="Hide descriptions"
        onChange={() => setMinimalMode(!minimalMode)}
        checked={minimalMode}
        sx={{ mb: 1, ml: 1 }}
      />
      {endpoints.filter((x) => x.methods.includes('POST')).map((endpoint) => (
        <Accordion
          key={endpoint.path}
          onChange={toggleAccordion(endpoint.path)}
          expanded={endpoint.path in expanded && expanded[endpoint.path]}
          value={expanded}
          elevation={0}
          square
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${endpoint.path}-content`}
            id={`${endpoint.path}-header`}
          >
            <Typography variant={endpoint.variant || 'body1'}>
              {endpoint.title || pathToTitle(endpoint.path)}
            </Typography>
            {endpoint.summary && (
              <Tooltip title={endpoint.summary} arrow>
                <IconButton size="small" sx={{ p: 0.5, ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ mt: -3 }}>
            {!minimalMode && endpoint.description}
            {!minimalMode && endpoint.pre && (
              <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                  {endpoint.pre}
                </MathJax>
              </MathJaxContext>
            )}
            {loading[endpoint.path] ? (
              <CircularProgress />
            ) : (
              <Response response={responses[endpoint.path]} />
            )}
            {!minimalMode && endpoint.post && (
              <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                  {endpoint.post}
                </MathJax>
              </MathJaxContext>
            )}
          </AccordionDetails>
          <AccordionActions sx={{ justifyContent: 'flex-start' }}>
            <EndpointArgs
              endpoint={endpoint}
              fetchDataForEndpoint={fetchDataForEndpoint}
              setEndpointArgs={setEndpointArgs}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%' }}>
              <IconButton
                onClick={() => handleOpenModal(endpoint)}
                size="small"
                aria-label="Open in Full Screen"
              >
                <FullscreenIcon />
              </IconButton>
            </Stack>
          </AccordionActions>
        </Accordion>
      ))}
      <Button onClick={collapseAll} sx={{ my: 2, mx: 1 }}>Collapse All</Button>
      <Button onClick={refreshAll} sx={{ my: 2, mx: 1 }}>Refresh All</Button>
    </Box>
  );
}
